import { Injectable, inject } from '@angular/core';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { PageTitleService } from './page-title.service';

/**
 * Custom Angular {@link TitleStrategy} that keeps the browser tab title in sync
 * with the active route on every navigation (Requirement #5506).
 *
 * It fires on each successful navigation — including child routes and any
 * URL-changing modal navigation — so the title never goes stale.
 *
 * Title source priority:
 *   1. An explicit `title` declared on a matched route (Angular-native; future
 *      per-route override hook — none declared today).
 *   2. A readable name derived from the deepest meaningful URL segment.
 *
 * Either way the result is a *baseline*: pages that render `<sub-header>` refine
 * it to the exact on-screen heading (with dynamic record names) on render. The
 * baseline ensures routes without a visible heading (login, account, etc.) still
 * get a unique, human-readable title.
 */
@Injectable()
export class AppPageTitleStrategy extends TitleStrategy {
    private static readonly UUID_REGEX =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    /**
     * Generic CRUD/action URL segments that say nothing about *which* record is
     * open (e.g. `.../income-losses/manage/508`). Skipping them lets the baseline
     * fall back to the meaningful parent segment (`income-losses` → "Income
     * Losses") instead of the useless "Manage". The page's `<sub-header>` later
     * refines this to the exact "{Entity Type} {ShortName}" once the record loads.
     * Wrapper routes like `manage-determination-docket-item` are matched by prefix.
     */
    private static readonly GENERIC_SEGMENTS = new Set<string>([
        'manage',
        'edit',
        'view',
        'create',
        'new',
        'add',
        'details',
        'detail',
    ]);

    private readonly _pageTitle = inject(PageTitleService);

    override updateTitle(snapshot: RouterStateSnapshot): void {
        const routeTitle = this.buildTitle(snapshot);
        const pageName = routeTitle ?? this.deriveFromUrl(snapshot.url);
        this._pageTitle.setNavigationBaseline(pageName);
    }

    /**
     * Derive a human-readable name from the deepest *meaningful* URL segment. Pure
     * numeric ids, UUID-like tokens, and generic action segments (manage/edit/…)
     * are skipped so a title never surfaces a raw slug/UUID or a bare "Manage" —
     * those routes are refined by the page's `<sub-header>`.
     */
    private deriveFromUrl(url: string): string | undefined {
        const segments = url
            .split('?')[0]
            .split('#')[0]
            .split('/')
            .filter((s) => s.length > 0);

        for (let i = segments.length - 1; i >= 0; i--) {
            const segment = this.safeDecode(segments[i]);
            if (this.isIdLike(segment) || this.isGenericActionSegment(segment)) {
                continue;
            }
            return this.deslugify(segment);
        }

        return undefined;
    }

    private isIdLike(segment: string): boolean {
        return /^\d+$/.test(segment) || AppPageTitleStrategy.UUID_REGEX.test(segment);
    }

    /**
     * True for bare action segments (`manage`, `edit`, …) and the wrapper-route
     * variants that embed them (`manage-determination-docket-item`,
     * `manage-vc-submission`). These never identify a record, so they are skipped.
     */
    private isGenericActionSegment(segment: string): boolean {
        const normalized = segment.toLowerCase();
        if (AppPageTitleStrategy.GENERIC_SEGMENTS.has(normalized)) {
            return true;
        }
        return [...AppPageTitleStrategy.GENERIC_SEGMENTS].some((g) => normalized.startsWith(`${g}-`));
    }

    private deslugify(segment: string): string {
        return segment
            .replace(/[-_]+/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    private safeDecode(value: string): string {
        try {
            return decodeURIComponent(value);
        } catch {
            return value;
        }
    }
}
